import { createRouter, createWebHistory } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store/user'
import { getPlatformName } from '@/utils/platformInfo'
import {
  clearSession,
  ensureFreshAccessToken,
  getAccessTokenExp,
  hasRefreshToken
} from '@/utils/sessionExpiryHandler'
import { getExpertReviewAccess } from '@/api/expertReview'
import { normalizeInitialBrowserLocation } from '@/utils/browserLocation'

// 守卫层 token 有效性校验：上次后端校验时间戳，5 分钟窗口内不重复打 /api/auth/me
const AUTH_CHECK_WINDOW_MS = 5 * 60 * 1000
let lastAuthCheckAt = 0

/**
 * 本地解码 JWT 取 exp（不引入 jwt-decode 依赖）。
 * 仅做快筛：exp 已过则判定过期，避免带着过期 token 进入页面后才被 401 拦截。
 * 解码失败（非 JWT / 格式异常）返回 null，交由后端校验兜底。
 */
export function getTokenExp(token) {
  return getAccessTokenExp(token)
}

function isInitialNavigation(from) {
  return Array.isArray(from?.matched) && from.matched.length === 0
}

function redirectToLoginWith(to, options = {}) {
  if (options.notify !== false) {
    ElMessage.warning('登录已过期，请重新登录')
  }
  return {
    path: to.path === '/mobile' || to.path.startsWith('/mobile/') ? '/mobile/login' : '/login',
    query: { redirect: to.fullPath },
    replace: true
  }
}

// 路由组件 - 使用懒加载优化性能
const Layout = () => import(/* webpackChunkName: "layout" */ '@/components/Layout.vue')
const MapView = () => import(/* webpackChunkName: "map" */ '@/views/MapViewRefactored.vue')
const AdminView = () => import(/* webpackChunkName: "admin" */ '@/views/AdminView.vue')
// AiReportView 已下线（Dify 断链，D4）
// const AiReportView = () => import('@/views/AiReportView.vue')
const ReportsView = () => import(/* webpackChunkName: "reports" */ '@/views/ReportsView.vue')
const EcologicalZonesView = () => import(/* webpackChunkName: "ecological-zones" */ '@/views/EcologicalZonesView.vue')
const SensitiveAssessmentView = () => import(/* webpackChunkName: "sensitive-assessment" */ '@/views/SensitiveAssessmentView.vue')
const HazardousWasteView = () => import(/* webpackChunkName: "hazardous-waste" */ '@/views/HazardousWasteView.vue')
const EmergencyCasesView = () => import(/* webpackChunkName: "emergency-cases" */ '@/views/EmergencyCasesView.vue')
const EventCaseView = () => import(/* webpackChunkName: "event-cases" */ '@/views/EventCaseView.vue')
const HazardousMaterialsView = () => import(/* webpackChunkName: "hazardous-materials" */ '@/views/HazardousMaterialsView.vue')
const ChemicalQueryView = () => import(/* webpackChunkName: "chemical-query" */ '@/views/ChemicalQueryView.vue')
const StandardsLibraryView = () => import(/* webpackChunkName: "standards-library" */ '@/views/StandardsLibraryView.vue')
const KnowledgeBaseView = () => import(/* webpackChunkName: "knowledge-base" */ '@/views/KnowledgeBaseView.vue')
// const NetworkTest = () => import(/* webpackChunkName: "network-test" */ '@/views/NetworkTest.vue')
// const NetworkTestSimple = () => import(/* webpackChunkName: "network-test-simple" */ '@/views/NetworkTestSimple.vue')
// const LayerManagementDebug = () => import(/* webpackChunkName: "layer-debug" */ '@/components/LayerManagementDebug.vue')
const DrawingModuleView = () => import(/* webpackChunkName: "drawing-module" */ '@/views/DrawingModuleView.vue')
const IconMappingTestView = () => import(/* webpackChunkName: "icon-mapping-test" */ '@/views/IconMappingTestView.vue')
const AIReportEngineTestView = () => import(/* webpackChunkName: "ai-report-test" */ '@/views/AIReportEngineTestView.vue')
const EvaluationDashboardView = () => import(/* webpackChunkName: "evaluation-dashboard" */ '@/views/EvaluationDashboardView.vue')
const AiDrawingViewRefactored = () => import(/* webpackChunkName: "ai-drawing-refactored" */ '@/views/AiDrawingViewRefactored.vue')
const ProDrawingWorkspace = () => import(/* webpackChunkName: "pro-drawing" */ '@/features/pro-drawing/views/ProDrawingWorkspace.vue')
const RefactoredDrawingView = () => import(/* webpackChunkName: "refactored-drawing" */ '@/views/RefactoredDrawingView.vue')
const RefactoredDrawingViewDebug = () => import(/* webpackChunkName: "refactored-drawing-debug" */ '@/views/RefactoredDrawingViewDebug.vue')
const ClearStorageView = () => import(/* webpackChunkName: "clear-storage" */ '@/views/ClearStorageView.vue')
const MaterialAllocationView = () => import(/* webpackChunkName: "material-allocation" */ '@/views/MaterialAllocationView.vue')
const HomeView = () => import(/* webpackChunkName: "home" */ '@/views/HomeView.vue')
const MonitorDashboardView = () => import(/* webpackChunkName: "monitor-dashboard" */ '@/views/MonitorDashboardView.vue')
const LoginView = () => import(/* webpackChunkName: "login" */ '@/views/LoginView.vue')
const PollutionDiffusionView = () => import(/* webpackChunkName: "pollution-diffusion" */ '@/views/PollutionDiffusionView.vue')
const UiPreviewView = () => import(/* webpackChunkName: "ui-preview" */ '@/views/UiPreviewView.vue')
const MapVisualSystemPreviewView = () => import(/* webpackChunkName: "map-visual-preview" */ '@/views/MapVisualSystemPreviewView.vue')
const SystemStatusView = () => import(/* webpackChunkName: "system-status" */ '@/views/SystemStatusView.vue')
const RouteManagementView = () => import(/* webpackChunkName: "route-management" */ '@/views/RouteManagementView.vue')
const DbMigrationView = () => import(/* webpackChunkName: "db-migration" */ '@/views/DbMigrationView.vue')
const ExpertReviewView = () => import(/* webpackChunkName: "expert-review" */ '@/views/ExpertReviewView.vue')
const ExpertReviewManageView = () => import(/* webpackChunkName: "expert-review-manage" */ '@/views/ExpertReviewManageView.vue')
const MobileLayout = () => import(/* webpackChunkName: "mobile-shell" */ '@/mobile/MobileLayout.vue')

const routes = [
  {
    path: '/mobile',
    component: MobileLayout,
    redirect: '/mobile/agent',
    children: [
      {
        path: 'launch',
        name: 'MobileLaunch',
        component: () => import(/* webpackChunkName: "mobile-launch" */ '@/mobile/views/MobileLaunchView.vue'),
        meta: { title: '正在打开本应用', public: true, hideMobileNav: true }
      },
      {
        path: 'login',
        name: 'MobileLogin',
        component: () => import(/* webpackChunkName: "mobile-login" */ '@/mobile/auth/MobileLoginView.vue'),
        meta: { title: '移动端登录', public: true, hideMobileNav: true }
      },
      {
        path: 'agent',
        name: 'MobileAgent',
        component: () => import(/* webpackChunkName: "mobile-agent" */ '@/mobile/views/MobileAgentRouteView.vue'),
        meta: { title: '应急智能体', mobileNav: 'agent' }
      },
      {
        path: 'database',
        name: 'MobileDatabase',
        component: () => import(/* webpackChunkName: "mobile-database" */ '@/mobile/views/MobileEmergencyDatabaseView.vue'),
        meta: { title: '应急数据库', mobileNav: 'database' }
      },
      {
        path: 'knowledge',
        name: 'MobileKnowledge',
        component: () => import(/* webpackChunkName: "mobile-knowledge" */ '@/mobile/views/MobileKnowledgeBaseView.vue'),
        meta: { title: '应急知识库', mobileNav: 'knowledge' }
      },
      {
        path: 'decision',
        name: 'MobileDecision',
        component: () => import(/* webpackChunkName: "mobile-decision" */ '@/mobile/views/MobileAIDecisionView.vue'),
        meta: { title: '辅助决策', mobileNav: 'decision' }
      },
      {
        path: 'profile',
        name: 'MobileProfile',
        component: () => import(/* webpackChunkName: "mobile-profile" */ '@/mobile/views/MobileProfileView.vue'),
        meta: { title: '我的', mobileNav: 'profile' }
      },
      {
        path: 'offline',
        name: 'MobileOffline',
        component: () => import(/* webpackChunkName: "mobile-offline" */ '@/mobile/views/MobileOfflineView.vue'),
        meta: { title: '网络不可用', public: true, hideMobileNav: true }
      }
    ]
  },
  // /map-visual-preview 路由已下线（D8）
  // {
  //   path: '/map-visual-preview',
  //   name: 'MapVisualSystemPreview',
  //   component: MapVisualSystemPreviewView,
  //   meta: { title: '地图视觉统一方案预览', standalone: true, public: true }
  // },
  // /ui-preview 路由已下线（D8）
  // {
  //   path: '/ui-preview',
  //   name: 'UiPreview',
  //   component: UiPreviewView,
  //   meta: { title: 'UI统一预览', standalone: true, public: true }
  // },
  {
    path: '/route-manager',
    name: 'RouteManager',
    component: RouteManagementView,
    meta: {
      title: '路由管理',
      standalone: true,
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/db-migration',
    name: 'DbMigration',
    component: DbMigrationView,
    meta: {
      title: '数据库迁移管理',
      standalone: true,
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/test',
    name: 'SystemStatus',
    component: SystemStatusView,
    meta: {
      title: '系统状态检测',
      standalone: true,
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: {
      title: '用户登录',
      standalone: true,
      public: true
    }
  },
  // AI 报告分享页（免登录查看分享报告）
  {
    path: '/share/report/:shareToken',
    name: 'SharedReport',
    component: () => import(/* webpackChunkName: "shared-report" */ '@/views/SharedReportView.vue'),
    meta: {
      title: '报告分享',
      standalone: true,
      public: true
    }
  },
  // /ai-report 已下线（Dify 断链，D4），重定向到 /aianalyse 兜底半月
  { path: '/ai-report', redirect: '/aianalyse' },
  {
    path: '/ecological-viewport-test',
    name: 'EcologicalZoneViewportTest',
    component: () => import('@/views/EcologicalZoneViewportTestView.vue'),
    meta: {
      title: '生态管控区视口同步测试',
      standalone: true,
      requiresAuth: true
    }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: AdminView,
    meta: {
      title: '数据管理后台',
      standalone: true,
      icon: 'DataBoard',
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  // /pollution-diffusion 路由已下线（D8）
  // { path: '/pollution-diffusion', name: 'PollutionDiffusion', component: PollutionDiffusionView, meta: { title: '污染扩散模拟', standalone: true, icon: 'TrendCharts' } },
  {
    path: '/emergency-scene/guangling-shahe',
    name: 'GuanglingShaheEmergencyScene',
    component: () => import(/* webpackChunkName: "emergency-scene" */ '@/views/EmergencySceneView.vue'),
    meta: { title: '广陵世家沙河三维应急推演', standalone: true, requiresAuth: true }
  },
  // 指挥调度中心路由（已重构）
  {
    path: '/command-center',
    name: 'CommandCenterList',
    component: () => import('@/views/CommandCenterList.vue'),
    meta: {
      title: '指挥调度中心',
      standalone: true,
      icon: 'Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/command-center/:eventUuid',
    name: 'CommandCenter',
    component: () => import('@/views/CommandCenterView.vue'),
    meta: {
      title: '应急指挥调度',
      standalone: true,
      icon: 'Monitor',
      requiresAuth: true
    }
  },
  {
    path: '/',
    component: Layout,
    children: [
      {
        path: '',
        redirect: '/home'
      },
      {
        path: 'home',
        name: 'Home',
        component: HomeView,
        meta: {
          title: '首页',
          icon: 'HomeFilled'
        }
      },
      {
        path: 'map',
        name: 'Map',
        component: MapView,
        meta: {
          title: '应急数据库',
          icon: 'Location'
        }
      },
      {
        path: 'aianalyse',
        name: 'aianalyse',
        component: () => import('@/views/AIDecisionSupportView.vue'),
        meta: {
          title: 'AI应急辅助决策',
          icon: 'Cpu'
        }
      },
      {
        path: 'jingjiang-three-level-facilities',
        name: 'JingjiangThreeLevelFacilities',
        component: () => import(/* webpackChunkName: "three-level-facilities" */ '@/views/ThreeLevelFacilityOverviewView.vue'),
        meta: { title: '靖江三级防控设施一览图', icon: 'Location', requiresAuth: true, hideAgentButton: true }
      },
      {
        path: 'taicang-three-level-review',
        name: 'TaicangThreeLevelReview',
        component: () => import(/* webpackChunkName: "three-level-review" */ '@/views/ThreeLevelPreventionReviewView.vue'),
        meta: {
          title: '太仓三级防控拓扑校核',
          icon: 'Connection',
          requiresAuth: true,
          hideAgentButton: true
        }
      },
      {
        path: 'information-report',
        name: 'InformationReport',
        component: () => import('@/views/InformationReportView.vue'),
        meta: { title: '信息报告', icon: 'DocumentChecked' }
      },
      // reports 路由已下线（D8）
      // { path: 'reports', name: 'Reports', component: ReportsView, meta: { title: '历史报告查询', icon: 'Document' } },
      // ecological-zones 路由已下线（D8）
      // { path: 'ecological-zones', name: 'EcologicalZones', component: EcologicalZonesView, meta: { title: '生态管控区地图', icon: 'Location' } },
      // sensitive-assessment 路由已下线（D8）
      // { path: 'sensitive-assessment', name: 'SensitiveAssessment', component: SensitiveAssessmentView, meta: { title: '重大敏感事件判定', icon: 'Warning' } },
      // hazardous-waste 路由已下线（D8）
      // { path: 'hazardous-waste', name: 'HazardousWaste', component: HazardousWasteView, meta: { title: '固废处置单位管理', icon: 'Grid' } },
      // emergency-cases 路由已下线（D8）
      // { path: 'emergency-cases', name: 'EmergencyCases', component: EmergencyCasesView, meta: { title: '应急案例库', icon: 'Files' } },
      // event-cases 路由已下线（D8）（组件仍被 KnowledgeBaseView 内部使用，不可删）
      // { path: 'event-cases', name: 'EventCases', component: EventCaseView, meta: { title: '事件案例库', icon: 'DataLine' } },
      // hazardous-materials 路由已下线（D8）
      // { path: 'hazardous-materials', name: 'HazardousMaterials', component: HazardousMaterialsView, meta: { title: '风险物质库', icon: 'Orange' } },
      // chemical-query 路由已下线（D8）（组件仍被 KnowledgeBaseView 内部使用，不可删）
      // { path: 'chemical-query', name: 'ChemicalQuery', component: ChemicalQueryView, meta: { title: '化学品查询', icon: 'Search', standalone: true } },
      // standards-library 路由已下线（D8）（组件仍被 KnowledgeBaseView 内部使用，不可删）
      // { path: 'standards-library', name: 'StandardsLibrary', component: StandardsLibraryView, meta: { title: '标准规范库', icon: 'Reading' } },
      {
        path: 'knowledge-base',
        name: 'KnowledgeBase',
        component: KnowledgeBaseView,
        meta: {
          title: '应急知识库',
          icon: 'Collection'
        }
      },
      {
        path: 'material-dosage',
        name: 'MaterialDosage',
        component: () => import(/* webpackChunkName: "material-dosage" */ '@/views/MaterialDosageView.vue'),
        meta: {
          title: 'AI应急资源调用',
          icon: 'Histogram'
        }
      },
      // material-allocation 路由已下线（D8）
      // { path: 'material-allocation', name: 'MaterialAllocation', component: MaterialAllocationView, meta: { title: '应急物资最优分配', icon: 'Truck' } },
      {
        path: 'drawing-refactored',
        name: 'DrawingRefactored',
        component: AiDrawingViewRefactored,
        meta: {
          title: '应急指挥一张图',
          icon: 'EditPen'
        }
      },
      // pro-drawing 路由已下线（D8）
      // { path: 'pro-drawing', name: 'ProDrawing', component: ProDrawingWorkspace, meta: { title: '专业绘图工作台', icon: 'Brush' } },
      // re-drawing 路由已下线（D8）
      // { path: 're-drawing', name: 'ReDrawing', component: RefactoredDrawingView, meta: { title: '重构版绘图工作台', icon: 'EditPen' } },
      // re-drawing-debug 路由已下线（D8）
      // { path: 're-drawing-debug', name: 'ReDrawingDebug', component: RefactoredDrawingViewDebug, meta: { title: '绘图工作台调试', icon: 'EditPen' } },
      // clear-storage 路由已下线（D8）
      // { path: 'clear-storage', name: 'ClearStorage', component: ClearStorageView, meta: { title: '清理缓存', icon: 'Delete' } },
      // icon-mapping-test 路由已下线（D8）
      // { path: 'icon-mapping-test', name: 'IconMappingTest', component: IconMappingTestView, meta: { title: '图标映射测试', icon: 'Picture' } },
      {
        path: 'monitor-dashboard',
        name: 'MonitorDashboard',
        component: MonitorDashboardView,
        meta: {
          title: '监控中心',
          icon: 'Monitor'
        }
      },
      // 应急智能体专用页面
      {
        path: 'agent',
        name: 'AgentChat',
        component: () => import('@/views/AgentChatView.vue'),
        meta: {
          title: '应急智能体',
          icon: 'Robot'
        }
      },
      {
        path: 'evaluation',
        name: 'EvaluationDashboard',
        component: EvaluationDashboardView,
        meta: {
          title: 'Agent 评测看板',
          icon: 'DataAnalysis',
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'expert-review',
        name: 'ExpertReview',
        component: ExpertReviewView,
        meta: {
          title: '我的专家评审',
          icon: 'DocumentChecked',
          requiresAuth: true,
          requiresExpertAssignment: true
        }
      },
      {
        path: 'expert-review/manage',
        name: 'ExpertReviewManage',
        component: ExpertReviewManageView,
        meta: {
          title: '专家评审管理',
          icon: 'UserFilled',
          requiresAuth: true,
          requiresAdmin: true
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/home'
  }
]

// createWebHistory 会立即读取浏览器地址，因此必须在创建路由实例前完成规范化。
normalizeInitialBrowserLocation()

const router = createRouter({
  history: createWebHistory(),
  routes
})

const platformName = getPlatformName()
const ADMIN_ROLES = new Set(['admin', 'administrator', 'super_admin', 'superadmin'])

function isAdminRole(role) {
  return ADMIN_ROLES.has(String(role || '').trim().toLowerCase())
}

function readStoredJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null')
  } catch (_) {
    return null
  }
}

function isCurrentAdmin(userStore) {
  return Boolean(
    userStore.isAdmin ||
    isAdminRole(userStore.user?.role) ||
    isAdminRole(readStoredJson('user')?.role)
  )
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 设置标题（直接使用平台名称，与 index.html 初始 <title> 一致，避免标签页跳变）
  document.title = platformName

  // 认证检查
  const userStore = useUserStore()
  const isLoginRoute = to.path === '/login' || to.path === '/mobile/login'
  const isPublic = to.meta.public || isLoginRoute
  const requiresAuth = to.meta.requiresAuth
  const requiresAdmin = to.meta.requiresAdmin
  const needsAuth = requiresAuth || !isPublic

  // 需要认证的路由先确保 Access Token 仍有足够有效期。已过期但 refreshToken
  // 有效时会静默轮换，不再因为页面跳转直接清空正在使用的会话。
  if (needsAuth) {
    const hadStoredSession = Boolean(userStore.isAuthenticated || hasRefreshToken())
    const activeToken = await ensureFreshAccessToken()
    if (!activeToken) {
      clearSession()
      userStore.token = ''
      userStore.user = null
      next(redirectToLoginWith(to, {
        // 首次打开平台只负责把用户送到登录页，不把历史会话失效提示
        // 当作本次操作产生的错误；使用过程中失效仍保留明确提醒。
        notify: hadStoredSession && !isInitialNavigation(from)
      }))
      return
    }
    userStore.token = activeToken

    // 后端校验 + 5 分钟时间窗口节流（Token 未到期但仍可能被服务端拒绝）
    const now = Date.now()
    if (now - lastAuthCheckAt > AUTH_CHECK_WINDOW_MS) {
      lastAuthCheckAt = now
      const isValid = await userStore.checkAuth({ redirectOnFail: false })
      if (!isValid) {
        // checkAuth(redirectOnFail:false) 不弹窗、不跳转，由守卫统一处理
        lastAuthCheckAt = 0
        next(redirectToLoginWith(to, { notify: !isInitialNavigation(from) }))
        return
      }
    }
  }

  if (needsAuth && (!userStore.user || (requiresAdmin && !isCurrentAdmin(userStore)))) {
    const isValid = await userStore.checkAuth({ redirectOnFail: false })
    if (!isValid) {
      next(redirectToLoginWith(to, { notify: false }))
      return
    }
  }

  // 需要管理员权限但不是管理员
  if (requiresAdmin && !isCurrentAdmin(userStore)) {
    next('/home')
    return
  }

  // 专家身份来自管理员指派，与账号角色解耦。管理员若未被指派，也不能读取题包。
  if (to.meta.requiresExpertAssignment) {
    try {
      const access = await getExpertReviewAccess()
      if (!access?.canReview) {
        next('/home')
        return
      }
    } catch (_) {
      next('/home')
      return
    }
  }

  // 已登录用户访问登录页
  if (isLoginRoute && (userStore.isAuthenticated || hasRefreshToken())) {
    const activeToken = await ensureFreshAccessToken()
    if (activeToken) {
      userStore.token = activeToken
      const redirect = typeof to.query.redirect === 'string'
        ? to.query.redirect
        : (to.path === '/mobile/login' ? '/mobile/agent' : '/')
      next(redirect)
      return
    }
    clearSession()
    userStore.token = ''
    userStore.user = null
  } else {
    next()
    return
  }

  next()
})

// 路由错误处理
router.onError((error) => {
  if (process.env.NODE_ENV === 'development') {
    // console.error('Router error:', error)
  }
})

export default router
